import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import crossIcon from '../../../assets/close.png';
import { useParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { onSlideChange } from '../../../graphql/subscriptions';
import { submitPollAnswer } from '../../../services/event/pollAnswerService';
import "./attendee-ppt-view.css";
import CpeCredit from '../../cpe-credit/cpe-credit';
import "../../../components/cpe-credit/cpe-credit.css";

const client = generateClient();

interface Slide {
    id: string;
    sequence: number;
    url: string;
    type: string;
    pollId?: string;
    pollResults?: PollResult[]; // Added pollResults for handling poll results
}

interface PollData {
    pollId: string;
    questionTitle: string;
    options: Record<string, string>;
}

interface PollResult {
    option: string;
    percentage: number;
    voteCount: number;
}

const PresentationViewer: React.FC<any> = ({ slidesData, pollData, allPollResultData, email }) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentSlide, setCurrentSlide] = useState<Slide | null>();
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false); 
    const slideContainerRef = useRef<HTMLDivElement>(null);
    const { eventId } = useParams<{ eventId: string }>();

    useEffect(() => {
        const subscription = client.graphql({ query: onSlideChange });
        const observer = subscription.subscribe({
            next: ({ data }) => {
                const newSlide = data.onSlideChange;
                if (newSlide && newSlide.id === eventId) {
                    const formattedSlide: Slide = {
                        id: newSlide.id,
                        sequence: newSlide.sequence,
                        url: newSlide.url,
                        type: newSlide.type,
                        pollId: newSlide.pollId,
                        pollResults: newSlide.pollResults?.filter((result: any) => result !== null).map((result: any) => ({
                            option: result.option,
                            percentage: result.percentage,
                            voteCount: result.voteCount
                        })),
                    };
                    setCurrentSlide(formattedSlide);
                    setIsSubmitted(false); // Reset submission state on slide change
                }
            },
            error: (error) => console.warn('Subscription error:', error),
        });

        return () => observer.unsubscribe();
    }, []);

    useEffect(() => {
      const defaultSlide = slidesData?.collaterals?.slides.find(
        (slide: Slide) => slide.sequence == 1
      );

      if (slidesData?.currentSlide) {
        setCurrentSlide(slidesData?.currentSlide);
      } else {
        setCurrentSlide(defaultSlide);
      }
    }, [eventId, slidesData]);


    useEffect(() => {
        if (currentSlide?.pollId) {
            // Check localStorage for previously submitted answer for the current poll
            const storedAnswer = localStorage.getItem(`poll_${currentSlide.pollId}`);
            if (storedAnswer) {
                setSelectedAnswer(storedAnswer);
                setIsSubmitted(true); // Hide submit button if answer exists in local storage
            } else {
                // Clear the selected answer if no answer exists in localStorage
                setSelectedAnswer(null);
                setIsSubmitted(false);
            }
        } else {
            // Clear everything if there's no pollId on the slide
            setSelectedAnswer(null);
            setIsSubmitted(false);
        }
    }, [currentSlide]);


    const toggleFullScreen = () => {
        const elem = currentSlide?.type === 'video' || currentSlide?.type === 'video/mp4'
            ? slideContainerRef.current?.querySelector('video')
            : slideContainerRef.current;

        if (!document.fullscreenElement) {
            if (elem && elem.requestFullscreen) {
                elem.requestFullscreen().catch(err => {
                    console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
                });
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(err => {
                    console.log(`Error attempting to exit full-screen mode: ${err.message} (${err.name})`);
                });
            }
        }
    };

    const handlePollSubmission = async () => {
        if (!currentSlide || !currentSlide.pollId || !selectedAnswer) return;

        try {
            await submitPollAnswer(currentSlide.pollId, selectedAnswer, email);
            setIsSubmitted(true); // Show thank-you message after successful submission

            // Store the submitted answer in localStorage
            localStorage.setItem(`poll_${currentSlide.pollId}`, selectedAnswer);

        } catch (error) {
            console.error('Error during poll submission:', error);
        }
    };

    const renderPollResults = () => {
        const pollResults = currentSlide?.pollResults || [];
        if (!pollResults.length) {
            return <p>Loading poll results...</p>;
        }

        return (
            <div className='poll-attendee-result'>
                <div className="attendee-section-wrapper">
                    <h3>Results</h3>
                    <div className='attendee-result-wrapper-box'>
                        {pollResults.map((result) => (
                            <div className='poll-result-attendee'>
                                <div key={result.option} className="poll-result-item attendee-side-wrapper">
                                    <div className="poll-option attendee-wrapper-option">{result.option}</div>
                                    <div className="poll-percentage">
                                        <div className="poll-bar" style={{ width: `${result.percentage}%`, backgroundColor: '#007bff' }}></div>
                                        <div className="poll-text attendee-side-text">{result.percentage}% ({result.voteCount} votes)</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const displayedSlide = currentSlide || slidesData?.collaterals?.slides.find((slide: Slide) => slide.sequence == 1);
    const poll = pollData.find((poll: PollData) => poll.pollId === currentSlide?.pollId);

    return (
        <div className="presentation-viewer" style={{ display: 'flex', flexDirection: 'column', width: '100%', background: '#242424' }}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: "center" }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'red', marginLeft: '10px' }}></span>
                    <h6 style={{ paddingTop: "10px", marginLeft: "10px", color: "white" }}>Live view</h6>
                </div>
                <div>
                    <img src={crossIcon} alt="Close icon" style={{ marginRight: '10px', width: '13px', height: '13px', cursor: 'pointer', display: "none" }} />
                </div>
            </div>
            {loading ? (
                <div className="w-100 h-100 border border-secondary d-flex justify-content-center align-items-center">
                    Loading slides...
                </div>
            ) : (
                <div ref={slideContainerRef} className="position-relative slide-container" style={{ width: '100%', height: '45vh', alignItems: 'center', overflow: 'hidden' }}>
                    <button onClick={toggleFullScreen} className="btn btn-light position-absolute" style={{ bottom: '10px', right: '10px', zIndex: 10 }}>
                        <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} style={{ fontSize: '1.2rem' }} />
                    </button>
                    <div>
                        {displayedSlide?.type === 'video' || displayedSlide?.type === 'video/mp4' ? (
                            <video autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}>
                                <source src={displayedSlide.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        ) : displayedSlide?.type === 'pollQuestions' && poll ? (
                            <div className="poll-container attendee-ppt-view-wrapper">
                                <h3 className='attendee-ppt-qurstion-wrapper'>Q.{poll.questionTitle}</h3>
                                {isSubmitted ? (
                                    <div className='submit-mesg'>
                                        <p>Thank you for your submission.</p>
                                        {/* {renderPollResults()} */}
                                    </div>
                                ) : (
                                    <div className='attendee-view-poll-wrapper'>
                                        <form>
                                            {Object.entries(poll.options).map(([key, value]) => (
                                                <div key={key} style={{ margin: '10px 0' }}>
                                                    <input
                                                        type="radio"
                                                        id={key}
                                                        name="poll"
                                                        value={key}
                                                        checked={selectedAnswer === key}
                                                        onChange={() => setSelectedAnswer(key)}
                                                    />
                                                    <label htmlFor={key} style={{ marginLeft: '8px' }}>{String(value)}</label>
                                                </div>
                                            ))}
                                        </form>
                                        {!isSubmitted && (
                                            <button onClick={handlePollSubmission} className="btn btn-primary mt-3 submit" disabled={!selectedAnswer}>
                                                Submit
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : displayedSlide?.type === 'pollresult' && displayedSlide.pollResults ? (
                            renderPollResults()
                        ) : displayedSlide?.type === 'cpeForm' ? (
                            <CpeCredit className="file3-classname"  />
                         ) : (
                            <img crossOrigin='anonymous' src={displayedSlide?.url || ''} alt={`Slide ${displayedSlide?.sequence || 'default'}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresentationViewer;
